import { useParams, Link } from "react-router-dom";
import { useState, useMemo } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/feature/ScrollToTop";
import MangaCard from "@/pages/home/components/MangaCard";
import { categories, categorySlugMap } from "@/mocks/categories";
import { getMangaByCategory } from "@/mocks/manga";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<"rating" | "popular" | "recent">("popular");

  const currentCategory = categories.find((c) => c.slug === slug);
  const mangaList = useMemo(() => {
    if (!slug) return [];
    const list = getMangaByCategory(slug);
    if (sortBy === "rating") return [...list].sort((a, b) => b.rating - a.rating);
    if (sortBy === "recent") return [...list].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    return [...list].sort((a, b) => b.views - a.views);
  }, [slug, sortBy]);

  const faqJsonLd = currentCategory ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: `迷妹漫画上有哪些${currentCategory.name}漫画？`,
        acceptedAnswer: {
          "@type": "Answer",
          text: `迷妹漫画的${currentCategory.name}漫画分类收录了多部优质${currentCategory.name}题材漫画，包括完结漫画和连载中漫画，全部支持免费下拉式在线阅读。`,
        },
      },
    ],
  } : null;

  if (!currentCategory) {
    return (
      <div className="min-h-screen bg-background-50 font-body">
        <Navbar />
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-20 text-center">
          <h1 className="text-xl font-heading font-semibold text-foreground-900 mb-3">分类未找到</h1>
          <Link to="/" className="text-sm text-primary-600 hover:text-primary-700">返回首页</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <ScrollToTop />
      {faqJsonLd && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />
      )}
      <div className="min-h-screen bg-background-50 font-body">
        <Navbar />
        <Breadcrumb items={[
          { label: "首页", href: "/" },
          { label: `${currentCategory.name}漫画` },
        ]} />

        <main>
          <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-8">
            <div className="mb-8">
              <h1 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-2">
                {currentCategory.name}漫画
              </h1>
              <p className="text-sm text-foreground-500">{currentCategory.description}</p>
            </div>

            {/* Category tabs */}
            <div className="flex flex-wrap gap-2 mb-6">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                    cat.slug === slug
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {/* Sort */}
            <div className="flex items-center gap-3 mb-6">
              <span className="text-xs text-foreground-500">排序：</span>
              {[
                { key: "popular" as const, label: "最热门" },
                { key: "rating" as const, label: "评分最高" },
                { key: "recent" as const, label: "最近更新" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setSortBy(opt.key)}
                  className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                    sortBy === opt.key
                      ? "bg-secondary-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {/* Manga grid */}
            {mangaList.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
                {mangaList.map((manga) => (
                  <MangaCard
                    key={manga.id}
                    slug={manga.slug}
                    title={manga.title}
                    coverUrl={manga.coverUrl}
                    status={manga.status}
                    rating={manga.rating}
                    category={categorySlugMap[manga.category] || manga.category}
                    views={manga.views}
                    chapters={manga.chapters.length}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <p className="text-sm text-foreground-500 mb-3">该分类下暂无漫画</p>
                <Link to="/" className="text-sm text-primary-600 hover:text-primary-700">返回首页</Link>
              </div>
            )}
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}